import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  type ColumnDef,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { AlertCircle, ChevronDown, ChevronUp, ChevronsUpDown, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { TopTabs } from '@/components/dashboard/TopTabs'
import { NewStocktakeDialog } from '@/components/stocktake/NewStocktakeDialog'
import {
  AdjustmentStatusBadge,
  CountStatusBadge,
} from '@/components/stocktake/StocktakeBadges'
import {
  OPEN_STOCKTAKE_STATUSES,
  useStocktakeList,
  type StocktakeSessionSummary,
} from '@/hooks/useStocktakes'
import { fmtDateTime } from '@/lib/dates'
import { cn } from '@/lib/utils'

type Tab = 'open' | 'archived'

const TABS: { id: Tab; label: string }[] = [
  { id: 'open', label: 'Open' },
  { id: 'archived', label: 'Archived' },
]

const OPEN = new Set<string>(OPEN_STOCKTAKE_STATUSES)

function TableSkeleton() {
  return (
    <div className="p-4 space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-9 w-full" />
      ))}
    </div>
  )
}

/** Stocky's "Type/Vendor" cell: the scan method and the variant filter, over the
 *  vendor the count covers. "All" means the whole vendor, not the whole store. */
function typeLabel(row: StocktakeSessionSummary): string {
  return `Barcode / ${row.filter_product_type || 'All'}`
}

const COLUMNS: ColumnDef<StocktakeSessionSummary>[] = [
  {
    accessorKey: 'session_id',
    id: 'session_id',
    header: 'No.',
    size: 80,
    cell: ({ row }) => (
      <span className="text-xs tabular-nums text-[var(--ink-2)]">
        #{row.original.session_id}
      </span>
    ),
    enableSorting: true,
  },
  {
    accessorKey: 'name',
    id: 'name',
    header: 'Name',
    cell: ({ row }) => (
      <span className="text-xs font-medium text-[var(--text-primary)]">{row.original.name}</span>
    ),
    enableSorting: true,
  },
  {
    id: 'type_vendor',
    header: 'Type/Vendor',
    size: 200,
    cell: ({ row }) => (
      <span className="flex flex-col leading-tight">
        <span className="text-xs text-[var(--text-primary)]">{row.original.filter_vendor || '—'}</span>
        <span className="text-[10px] text-[var(--mute)]">{typeLabel(row.original)}</span>
      </span>
    ),
    enableSorting: false,
  },
  {
    accessorKey: 'location_name',
    id: 'location_name',
    header: 'Location',
    size: 150,
    cell: ({ row }) => (
      <span className="text-xs text-[var(--ink-2)]">
        {row.original.location_name || '—'}
      </span>
    ),
    enableSorting: true,
  },
  {
    accessorKey: 'created_at',
    id: 'created_at',
    header: 'Generated At',
    size: 170,
    cell: ({ row }) => (
      <span className="text-xs tabular-nums whitespace-nowrap text-[var(--mute)]">
        {fmtDateTime(row.original.created_at) || '—'}
      </span>
    ),
    enableSorting: true,
  },
  {
    id: 'count_status',
    header: 'Count Status',
    size: 150,
    cell: ({ row }) => <CountStatusBadge status={row.original.status} />,
    enableSorting: false,
  },
  {
    id: 'adjustment_status',
    header: 'Adjustment Status',
    size: 150,
    cell: ({ row }) => <AdjustmentStatusBadge status={row.original.status} />,
    enableSorting: false,
  },
  {
    accessorKey: 'total_lines',
    id: 'total_lines',
    header: 'Items',
    size: 80,
    meta: { numeric: true },
    cell: ({ row }) => (
      <span className="text-xs tabular-nums text-[var(--ink-2)]">
        {row.original.total_lines}
      </span>
    ),
    enableSorting: true,
  },
]

export default function Stocktakes() {
  const navigate = useNavigate()
  const [newOpen, setNewOpen] = React.useState(false)
  const [tab, setTab] = React.useState<Tab>('open')
  // One fetch, split client-side: the two tabs are four statuses between them,
  // and the list is small enough that a round trip per tab buys nothing.
  const { data: sessions = [], isLoading, isError, refetch } = useStocktakeList()
  const [sorting, setSorting] = React.useState<SortingState>([
    { id: 'session_id', desc: true },
  ])

  const openCount = sessions.filter((s) => OPEN.has(s.status)).length
  const rows = React.useMemo(
    () =>
      sessions.filter((s) => (tab === 'open' ? OPEN.has(s.status) : !OPEN.has(s.status))),
    [sessions, tab]
  )

  const table = useReactTable({
    data: rows,
    columns: COLUMNS,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  return (
    <div className="brand-theme flex flex-col h-screen bg-[var(--surface-page)] text-[var(--text-primary)]">
      <TopTabs />
      {/* Page header */}
      <div className="flex items-center gap-3 border-b border-[var(--line-warm)] px-5 py-3.5 bg-[var(--surface-card)]">
        <h1 className="text-lg font-semibold tracking-tight text-[var(--text-primary)]">
          Stocktakes
        </h1>
        <div className="flex-1" />
        <Button
          size="sm"
          className="h-7 text-xs"
          onClick={() => setNewOpen(true)}
          data-testid="new-stocktake-btn"
        >
          Create Stocktake
        </Button>
      </div>

      {/* Open / Archived */}
      <div className="flex h-9 items-stretch border-b border-[var(--line-warm)] bg-[var(--surface-card)]">
        {TABS.map((t) => {
          const active = tab === t.id
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={cn(
                'flex items-center gap-1.5 px-3.5 text-xs border-r border-[var(--line-warm)] transition-colors',
                active
                  ? 'text-[var(--text-primary)] font-semibold'
                  : 'text-[var(--ink-2)] hover:text-[var(--text-primary)]'
              )}
              style={active ? { boxShadow: 'inset 0 -2px 0 var(--text-primary)' } : undefined}
              data-testid={`stocktake-tab-${t.id}`}
            >
              {t.label}
              <span className="text-[10px] text-[var(--mute)]">
                {t.id === 'open' ? openCount : sessions.length - openCount}
              </span>
            </button>
          )
        })}
      </div>

      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        {isLoading && <TableSkeleton />}

        {isError && !isLoading && (
          <div className="flex justify-center py-12">
            <Card className="w-80 rounded-none shadow-none border-[var(--line-warm)] bg-[var(--surface-card)]">
              <CardContent className="flex flex-col items-center gap-3 pt-6 pb-5">
                <AlertCircle className="h-8 w-8 text-[var(--status-rejected-fg)]" />
                <p className="text-sm text-[var(--ink-2)] text-center">
                  Failed to load stocktakes
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs"
                  onClick={() => refetch()}
                >
                  <RefreshCw className="mr-1.5 h-3 w-3" />
                  Retry
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {!isLoading && !isError && rows.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
            <p className="text-sm text-[var(--ink-2)]">
              {tab === 'open' ? 'No open stocktakes' : 'No archived stocktakes'}
            </p>
            {tab === 'open' && (
              <Button
                size="sm"
                className="mt-2 h-7 text-xs"
                onClick={() => setNewOpen(true)}
              >
                Create Stocktake
              </Button>
            )}
          </div>
        )}

        {!isLoading && !isError && rows.length > 0 && (
          <div className="overflow-auto flex-1">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((hg) => (
                  <TableRow key={hg.id} className="hover:bg-transparent">
                    {hg.headers.map((header) => {
                      const numeric =
                        (header.column.columnDef.meta as { numeric?: boolean } | undefined)
                          ?.numeric ?? false
                      return (
                        <TableHead
                          key={header.id}
                          className={cn(
                            'sticky top-0 z-10',
                            numeric && 'text-right',
                            header.column.getCanSort() && 'cursor-pointer select-none'
                          )}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          <span
                            className={cn(
                              'inline-flex items-center gap-1',
                              numeric && 'justify-end w-full'
                            )}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                            {header.column.getCanSort() && (
                              <span className="opacity-50">
                                {header.column.getIsSorted() === 'asc' ? (
                                  <ChevronUp className="h-3 w-3" />
                                ) : header.column.getIsSorted() === 'desc' ? (
                                  <ChevronDown className="h-3 w-3" />
                                ) : (
                                  <ChevronsUpDown className="h-3 w-3" />
                                )}
                              </span>
                            )}
                          </span>
                        </TableHead>
                      )
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.map((row, index) => (
                  <TableRow
                    key={row.id}
                    className={cn(
                      'cursor-pointer border-b border-[var(--line-warm)]',
                      index % 2 === 1
                        ? 'bg-[var(--surface-zebra)]'
                        : 'bg-[var(--surface-card)]'
                    )}
                    // The whole row is the target — opening the stocktake is the
                    // one thing you do with it, so there's nothing else to click.
                    onClick={() => navigate(`/stocktakes/${row.original.session_id}`)}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        navigate(`/stocktakes/${row.original.session_id}`)
                      }
                    }}
                    aria-label={`Open stocktake ${row.original.name}`}
                    data-testid={`stocktake-session-row-${row.original.session_id}`}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const numeric =
                        (cell.column.columnDef.meta as { numeric?: boolean } | undefined)
                          ?.numeric ?? false
                      return (
                        <TableCell
                          key={cell.id}
                          className={cn('py-1.5 px-2 text-xs', numeric && 'text-right tabular-nums')}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <NewStocktakeDialog open={newOpen} onOpenChange={setNewOpen} />
    </div>
  )
}
